import React, { ChangeEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NctsCountry } from 'config'
import FormModal from '../../../../shared/components/FormModal'
import {
  CANCELLATION_REASON_MAX_LENGTH,
  isValidCancellationReason,
  requiresUserWrittenCancellationReason,
} from '../models/cancellationReason'

interface CancelTransitOperationModalProps {
  show: boolean
  country: NctsCountry | null
  onClose: () => void
  onSubmit: (reason: string) => void
}

function CancelTransitOperationModal({
  show,
  country,
  onClose,
  onSubmit,
}: Readonly<CancelTransitOperationModalProps>) {
  const { t } = useTranslation()
  const [reason, setReason] = useState<string>('')

  const isEditable = requiresUserWrittenCancellationReason(country)
  const defaultReason = isEditable ? '' : t('transitOperation.cancel.defaultReason')

  useEffect(() => {
    if (show) {
      setReason((current) => (isEditable ? current || defaultReason : defaultReason))
    }
  }, [show, defaultReason, isEditable])

  const isReasonValid = isValidCancellationReason(reason)

  return (
    <FormModal show={show} title={t('transitOperation.cancel.modalTitle')} onClose={onClose}>
      <div className="d-flex flex-column align-self-center w-100 mb-1">
        <p>{t('transitOperation.cancel.reasonLabel')}</p>
        <textarea
          className="w-100 form-control mb-2"
          maxLength={CANCELLATION_REASON_MAX_LENGTH}
          value={reason}
          disabled={!isEditable}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
        />
        {isEditable && reason !== '' && !isReasonValid && (
          <small className="text-danger">{t('transitOperation.cancel.reasonInvalid')}</small>
        )}
        <small className="text-black-50">{t('transitOperation.cancel.sentToCustomsHint')}</small>
      </div>
      <div className="d-flex align-content-end justify-content-end mt-5">
        <button
          type="button"
          className="btn btn-lg btn-primary mx-1 text-black shadow-sm"
          onClick={() => onSubmit(reason)}
          disabled={!isReasonValid}
        >
          <span>{t('buttons.submit')}</span>
        </button>
      </div>
    </FormModal>
  )
}

export default CancelTransitOperationModal
